import { CdkDrag, CdkDragDrop, CdkDragPreview, CdkDropList } from '@angular/cdk/drag-drop';
import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal,
  signal,
  TemplateRef,
  viewChild,
  WritableSignal
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoPipe } from '@jsverse/transloco';
import type { GridHeaderSelectionState } from '../../contracts/grid-row-selection.interface';
import { DataGridSelectionStore } from '../selection/data-grid-selection.store';
import { DeightonChipComponent } from '@deighton/shared-ui/layout';
import type { ColumnFilter, ColumnFilterMap } from '../column-filter.types';
import { DataGridColumnResizeDirective, type DataGridColumnResizeWidth } from '../column-resize.directive';
import {
  gridTemplateColumnsFor,
  resolveColumnWidth,
  totalColumnWidth
} from '../data-grid-column-layout.utils';
import {
  DATA_GRID_GROUP_BAR_DROP_LIST_ID,
  getCellRawValue,
  getCellValue,
  getColorForValue,
  resolveDiscriminatorColor,
  sortIconFor
} from '../data-grid-grouping.utils';
import {
  DATA_GRID_FLAT_SORT_PATH,
  DATA_GRID_LOAD_MORE_BUFFER,
  DATA_GRID_ROW_HEIGHT,
  DATA_GRID_VIRTUALIZATION_THRESHOLD,
  getLeafViewportVisibleHeight,
  groupHasMorePages,
  resolveLoadMoreValue,
  shouldLoadMoreOnScroll,
  shouldVirtualizeGroupRows
} from '../data-grid-virtual-scroll.utils';
import {
  DEFAULT_COLUMN_WIDTH,
  DEFAULT_DATE_FORMAT,
  DataGridColumn,
  DataGridColumnDragData,
  DataGridGroupBucketViewState,
  DataGridGroupSortState,
  SortDirection
} from '../data-grid.types';

/** Header click requesting a sort cycle for a field in a table instance. */
export interface DataGridHeaderSortEvent {
  /** Column field to cycle. */
  readonly field: string;
  /** Flat path or composite group path. */
  readonly sortPath: string;
}

/** CDK column-reorder drop within a table header. */
export interface DataGridColumnReorderEvent {
  /** CDK drop event. */
  readonly event: CdkDragDrop<Array<string>, Array<string>, DataGridColumnDragData>;
  /** Flat path or composite group path. */
  readonly sortPath: string;
  /** Visible columns for this table instance. */
  readonly columns: Array<DataGridColumn>;
}

/** Request to open the column filter menu from a header button. */
export interface DataGridFilterMenuOpenEvent {
  /** Click event used for positioning. */
  readonly mouseEvent: MouseEvent;
  /** Column whose filter is being edited. */
  readonly column: DataGridColumn;
}

/**
 * Shared header + body for flat and grouped-leaf tables, including virtual scroll.
 */
@Component({
  selector: 'd-data-grid-table',
  imports: [
    DatePipe,
    DeightonChipComponent,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatCheckboxModule,
    NgTemplateOutlet,
    TranslocoPipe,
    CdkDrag,
    CdkDropList,
    CdkDragPreview,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    DataGridColumnResizeDirective
  ],
  templateUrl: './data-grid-table.component.html',
  styleUrl: './data-grid-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'dg-table-host',
    '[style.--dg-total-col-width.px]': 'totalWidth()'
  }
})
export class DataGridTableComponent {
  /** Table wrapper used for ResizeObserver (host is `display: contents`). */
  private readonly tableWrapper: Signal<ElementRef<HTMLElement> | undefined> = viewChild('tableWrapper', {
    read: ElementRef
  });

  /** CDK viewport for this table instance, when virtualized. */
  private readonly viewport: Signal<CdkVirtualScrollViewport | undefined> =
    viewChild(CdkVirtualScrollViewport);

  /** Visible columns for this table instance. */
  public columns: InputSignal<Array<DataGridColumn>> = input<Array<DataGridColumn>>(
    new Array<DataGridColumn>()
  );

  /** Sorted rows to render. */
  public rows: InputSignal<Array<Record<string, unknown>>> = input<Array<Record<string, unknown>>>(
    new Array<Record<string, unknown>>()
  );

  /** Flat path or composite group path. */
  public sortPath: InputSignal<string> = input<string>(DATA_GRID_FLAT_SORT_PATH);

  /** Thead drop-list id. */
  public theadId: InputSignal<string> = input<string>('thead-flat');

  /** Sticky header offset in pixels. */
  public stickyTop: InputSignal<number> = input<number>(0);

  /** Number of leading shared columns (rest are type-specific). */
  public sharedCount: InputSignal<number> = input<number>(0);

  /** Loaded row count for virtualize / load-more decisions. */
  public rowCount: InputSignal<number> = input<number>(0);

  /** Server-reported total for this bucket. */
  public totalCount: InputSignal<number> = input<number>(0);

  /** Width of the leading selection checkbox column, or 0. */
  public selectionColumnWidth: InputSignal<number> = input<number>(0);

  /** Width of the leading row-action column, or 0. */
  public actionColumnWidth: InputSignal<number> = input<number>(0);

  /** Custom row-action template from the host. */
  public rowActionTemplate: InputSignal<TemplateRef<{ $implicit: Record<string, unknown> }> | null> =
    input<TemplateRef<{ $implicit: Record<string, unknown> }> | null>(null);

  /** Whether the default fly-to button should render. */
  public showDefaultRowAction: InputSignal<boolean> = input<boolean>(false);

  /** Fixed row height for virtual scroll. */
  public rowHeight: InputSignal<number> = input<number>(DATA_GRID_ROW_HEIGHT);

  /** Key of the hovered map feature. */
  public hoveredRowKey: InputSignal<number | null> = input<number | null>(null);

  /** Active column filters, for header filter-button state. */
  public columnFilters: InputSignal<ColumnFilterMap> = input<ColumnFilterMap>(
    new Map<string, ColumnFilter>()
  );

  /** Fields currently in the group chain. */
  public groupedFields: InputSignal<Array<string>> = input<Array<string>>(new Array<string>());

  /** Whether column filter buttons should render. */
  public columnFiltersEnabled: InputSignal<boolean> = input<boolean>(false);

  /** Fields allowed in the group chain. Empty means every shared column is groupable. */
  public groupableFields: InputSignal<Array<string>> = input<Array<string>>(new Array<string>());

  /** Whether grouping chrome (group bar + groupable header icons) is enabled. */
  public groupingEnabled: InputSignal<boolean> = input<boolean>(false);

  /** Whether the flat view has more server pages. */
  public hasMore: InputSignal<boolean> = input<boolean>(false);

  /** Per-bucket loading / hasNextPage flags. */
  public groupBucketStates: InputSignal<Record<string, DataGridGroupBucketViewState>> = input<
    Record<string, DataGridGroupBucketViewState>
  >({});

  /** Whether a primary fetch is in flight. */
  public loading: InputSignal<boolean> = input<boolean>(false);

  /** Whether a pagination request is in flight. */
  public loadingMore: InputSignal<boolean> = input<boolean>(false);

  /** Outermost group field, or null in flat mode. */
  public primaryGroupField: InputSignal<string | null> = input<string | null>(null);

  /** Persisted column widths. */
  public columnWidths: InputSignal<Record<string, number>> = input<Record<string, number>>({});

  /** Per-group sort state. */
  public sortStates: InputSignal<Record<string, DataGridGroupSortState | null>> = input<
    Record<string, DataGridGroupSortState | null>
  >({});

  /** Flat sort state. */
  public flatSortState: InputSignal<DataGridGroupSortState | null> = input<DataGridGroupSortState | null>(
    null
  );

  /** Color map for discriminator chips. */
  public discriminatorColors: InputSignal<Record<string, string>> = input<Record<string, string>>({});

  /** Slug → display name map for discriminator chips. */
  public discriminatorLabels: InputSignal<Record<string, string>> = input<Record<string, string>>({});

  /** Field → column map for resize reset fallback. */
  public fieldToColumnMap: InputSignal<Map<string, DataGridColumn>> = input<Map<string, DataGridColumn>>(
    new Map<string, DataGridColumn>()
  );

  /** Emits when the user cycles sort on a header. */
  public headerSort: OutputEmitterRef<DataGridHeaderSortEvent> = output<DataGridHeaderSortEvent>();

  /** Emits when columns are reordered within the thead. */
  public columnReorder: OutputEmitterRef<DataGridColumnReorderEvent> = output<DataGridColumnReorderEvent>();

  /** Emits when a header filter button is clicked. */
  public filterMenuOpen: OutputEmitterRef<DataGridFilterMenuOpenEvent> =
    output<DataGridFilterMenuOpenEvent>();

  /** Emits the hovered row key, or null on leave. */
  public rowHover: OutputEmitterRef<number | null> = output<number | null>();

  /** Emits the row when the default fly-to action is clicked. */
  public rowAction: OutputEmitterRef<Record<string, unknown>> = output<Record<string, unknown>>();

  /** Emits a path or null when the viewport needs another page. */
  public loadMore: OutputEmitterRef<string | null> = output<string | null>();

  /** Emits the committed width after a resize drag or reset. */
  public columnWidthChange: OutputEmitterRef<{ field: string; width: number }> = output<{
    field: string;
    width: number;
  }>();

  /** Emits true while a resize drag is in progress. */
  public resizeActive: OutputEmitterRef<boolean> = output<boolean>();

  /** Per-grid selection store provided by `d-data-grid`. */
  private readonly selectionStore: DataGridSelectionStore = inject(DataGridSelectionStore);

  /** DestroyRef for observer teardown. */
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  /** Live widths during an in-progress resize. */
  private readonly activeResizeWidths: WritableSignal<Record<string, number>> = signal<
    Record<string, number>
  >({});

  /** Suppresses the click-to-sort that fires after a resize mouseup. */
  private readonly resizeDragOccurred: WritableSignal<boolean> = signal<boolean>(false);

  /** Whether this instance should virtualize its body. */
  protected shouldVirtualize: Signal<boolean> = computed((): boolean => {
    const path: string = this.sortPath();
    const rowCount: number = this.rowCount();
    const totalCount: number = this.totalCount();
    const bucketHasMore: boolean = groupHasMorePages(path, rowCount, totalCount, this.groupBucketStates());
    return shouldVirtualizeGroupRows(
      rowCount,
      path,
      DATA_GRID_VIRTUALIZATION_THRESHOLD,
      this.hasMore(),
      bucketHasMore
    );
  });

  /** Total min-width for the table wrapper. */
  protected totalWidth: Signal<number> = computed((): number =>
    totalColumnWidth(
      this.columns(),
      this.actionColumnWidth(),
      (col: DataGridColumn): number => this.resolvedWidth(col),
      this.selectionColumnWidth()
    )
  );

  /** CDK drop-list id of the group bar, used to connect thead drag sources. */
  protected readonly groupBarDropListId: string = DATA_GRID_GROUP_BAR_DROP_LIST_ID;

  /** Flat sort-path sentinel. */
  protected readonly flatSortPath: string = DATA_GRID_FLAT_SORT_PATH;

  /** Default date format when a column has no `dateFormat`. */
  protected readonly defaultDateFormat: string = DEFAULT_DATE_FORMAT;

  /** Default column width used when resetting a resize. */
  protected readonly defaultColumnWidth: number = DEFAULT_COLUMN_WIDTH;

  /** Cached grid-template-columns string. */
  private gridTemplateCacheRef: Array<DataGridColumn> | null = null;

  /** Fingerprint of widths used to build the cached template string. */
  private gridTemplateCacheFingerprint: string = '';

  /** Cached `grid-template-columns` value. */
  private gridTemplateCacheValue: string = '';

  /**
   * Initializes DataGridTableComponent and observes wrapper size for CDK viewport refresh.
   */
  constructor() {
    effect((): void => {
      void this.rows();
      void this.columns();
      queueMicrotask((): void => this.refreshViewport());
    });

    this.observeTableWrapperSize();
  }

  /**
   * Resolves the effective width for a column, including live resize.
   *
   * @param col - The column definition.
   * @returns Width in pixels.
   */
  protected resolvedWidth(col: DataGridColumn): number {
    return resolveColumnWidth(col, this.activeResizeWidths(), this.columnWidths(), this.defaultColumnWidth);
  }

  /**
   * Returns a memoized CSS `grid-template-columns` string.
   *
   * @param columns - Visible columns for this row.
   * @returns Grid track list.
   */
  protected gridTemplateColumns(columns: Array<DataGridColumn>): string {
    const actionWidth: number = this.actionColumnWidth();
    const selectionWidth: number = this.selectionColumnWidth();
    const fingerprint: string =
      selectionWidth +
      '|' +
      actionWidth +
      '|' +
      columns.map((col: DataGridColumn): string => col.field + ':' + this.resolvedWidth(col)).join(',');
    if (columns === this.gridTemplateCacheRef && fingerprint === this.gridTemplateCacheFingerprint) {
      return this.gridTemplateCacheValue;
    }
    const value: string = gridTemplateColumnsFor(
      columns,
      actionWidth,
      (col: DataGridColumn): number => this.resolvedWidth(col),
      selectionWidth
    );
    this.gridTemplateCacheRef = columns;
    this.gridTemplateCacheFingerprint = fingerprint;
    this.gridTemplateCacheValue = value;
    return value;
  }

  /**
   * Visible height for a grouped leaf viewport.
   *
   * @param rowCount - Loaded row count.
   * @returns Height in pixels.
   */
  protected leafViewportHeight(rowCount: number): number {
    return getLeafViewportVisibleHeight(rowCount, this.rowHeight());
  }

  /**
   * Current sort direction for a header, or null.
   *
   * @param field - Column field.
   * @returns Sort direction, or null when unsorted.
   */
  protected columnSortDir(field: string): SortDirection | null {
    const sortPath: string = this.sortPath();
    const state: DataGridGroupSortState | null =
      sortPath === DATA_GRID_FLAT_SORT_PATH ? this.flatSortState() : (this.sortStates()[sortPath] ?? null);
    return state?.field === field ? state.dir : null;
  }

  /**
   * Sort icon for a header.
   *
   * @param field - Column field.
   * @returns Material icon ligature.
   */
  protected sortIcon(field: string): string {
    return sortIconFor(this.columnSortDir(field));
  }

  /**
   * Whether a field is in the active group chain.
   *
   * @param field - Column field.
   * @returns True when grouped.
   */
  protected isGroupedByField(field: string): boolean {
    return this.groupedFields().includes(field);
  }

  /**
   * Whether a field is allowed to be used for grouping.
   *
   * @param field - Column field.
   * @returns True when groupable.
   */
  protected isGroupableField(field: string): boolean {
    const allowed: Array<string> = this.groupableFields();
    return allowed.length === 0 || allowed.includes(field);
  }

  /**
   * Whether the groupable-column icon should render for a header.
   * Only shared columns can be dragged onto the group bar.
   *
   * @param field - Column field.
   * @param columnIndex - Index in the visible column list.
   * @returns True when the icon should show.
   */
  protected showGroupableIcon(field: string, columnIndex: number): boolean {
    if (!this.groupingEnabled()) {
      return false;
    }
    if (columnIndex >= this.sharedCount()) {
      return false;
    }
    return this.isGroupableField(field);
  }

  /**
   * Transloco key for the groupable-column header tooltip.
   *
   * @param field - Column field.
   * @returns Tooltip translation key.
   */
  protected groupableColumnTooltipKey(field: string): string {
    return this.isGroupedByField(field)
      ? 'components.dataGrid.groupedColumnTooltip'
      : 'components.dataGrid.groupableColumnTooltip';
  }

  /**
   * Handles a header click, suppressing the synthetic click after a resize drag.
   *
   * @param field - Column field.
   */
  protected onHeaderSort(field: string): void {
    if (this.resizeDragOccurred()) {
      return;
    }
    this.headerSort.emit({ field, sortPath: this.sortPath() });
  }

  /**
   * Forwards a within-table column reorder.
   *
   * @param event - CDK drop event.
   */
  protected onColumnReorder(event: CdkDragDrop<Array<string>, Array<string>, DataGridColumnDragData>): void {
    this.columnReorder.emit({ event, sortPath: this.sortPath(), columns: this.columns() });
  }

  /**
   * Opens the filter menu for a column.
   *
   * @param event - Click event.
   * @param col - Column definition.
   */
  protected onFilterMenuOpen(event: MouseEvent, col: DataGridColumn): void {
    event.stopPropagation();
    this.filterMenuOpen.emit({ mouseEvent: event, column: col });
  }

  /**
   * Applies a live resize width during drag.
   *
   * @param event - Field and live width.
   */
  protected onLiveWidth(event: DataGridColumnResizeWidth): void {
    this.activeResizeWidths.update((current: Record<string, number>): Record<string, number> => ({
      ...current,
      [event.field]: event.width
    }));
  }

  /**
   * Marks that the resize pointer actually moved, so the following click is ignored.
   */
  protected onResizeDragOccurred(): void {
    this.resizeDragOccurred.set(true);
  }

  /**
   * Commits the final resize width.
   *
   * @param event - Field and committed width.
   */
  protected onWidthCommit(event: DataGridColumnResizeWidth): void {
    if (this.resizeDragOccurred()) {
      requestAnimationFrame((): void => {
        this.resizeDragOccurred.set(false);
      });
    }
    this.activeResizeWidths.update((current: Record<string, number>): Record<string, number> => {
      const rest: Record<string, number> = { ...current };
      delete rest[event.field];
      return rest;
    });
    this.columnWidthChange.emit(event);
  }

  /**
   * Resets a column to its definition width.
   *
   * @param field - Column field.
   */
  protected onWidthReset(field: string): void {
    this.columnWidthChange.emit({
      field,
      width: this.fieldToColumnMap().get(field)?.width ?? this.defaultColumnWidth
    });
  }

  /**
   * Map-feature key used for hover highlighting.
   *
   * @param row - Data row.
   * @returns Numeric key, or null when the row has no key.
   */
  protected rowHoverKey(row: Record<string, unknown>): number | null {
    const key: unknown = row['key'];
    return typeof key === 'number' ? key : null;
  }

  /**
   * Whether the row should show the map-hover highlight.
   * Both sides must be a real key so `null === null` does not highlight every row.
   *
   * @param row - Data row.
   * @returns True when this row's key matches the hovered map feature.
   */
  protected isRowHighlighted(row: Record<string, unknown>): boolean {
    const hovered: number | null = this.hoveredRowKey();
    if (hovered === null) {
      return false;
    }
    return this.rowHoverKey(row) === hovered;
  }

  /**
   * Whether row selection is enabled for this table instance.
   *
   * @returns True when the selection column has a width.
   */
  protected selectionEnabled(): boolean {
    return this.selectionColumnWidth() > 0;
  }

  /**
   * Whether the column-header select-all checkbox should render.
   * Grouped leaves already have group-header checkboxes; the header checkbox
   * is only useful in the ungrouped flat view.
   *
   * @returns True when selection is enabled and this instance is the flat table.
   */
  protected showHeaderCheckbox(): boolean {
    return this.selectionEnabled() && this.sortPath() === DATA_GRID_FLAT_SORT_PATH;
  }

  /**
   * Whether a data row is currently selected.
   *
   * @param row - Data row.
   * @returns True when selected.
   */
  protected isRowSelected(row: Record<string, unknown>): boolean {
    if (!this.selectionEnabled()) {
      return false;
    }
    const key: string | null = this.selectionStore.keyOf(row);
    if (key === null) {
      return false;
    }
    return this.selectionStore.isSelected(key, this.sortPath());
  }

  /**
   * Header checkbox tri-state for this table.
   *
   * @returns `none`, `some`, or `all`.
   */
  protected headerSelectionState(): GridHeaderSelectionState {
    if (!this.selectionEnabled()) {
      return 'none';
    }
    return this.selectionStore.headerState(this.sortPath(), this.rows());
  }

  /**
   * Whether the header checkbox is fully checked.
   *
   * @returns True when every loaded row is selected.
   */
  protected isHeaderChecked(): boolean {
    return this.headerSelectionState() === 'all';
  }

  /**
   * Whether the header checkbox is indeterminate.
   *
   * @returns True when some but not all loaded rows are selected.
   */
  protected isHeaderIndeterminate(): boolean {
    return this.headerSelectionState() === 'some';
  }

  /**
   * Toggles the header checkbox between select-all-loaded and deselect.
   * Click propagation is stopped on the checkbox in the template so the header does not sort.
   */
  protected onHeaderCheckboxToggle(): void {
    this.selectionStore.toggleSelectAllLoaded(this.sortPath(), this.rows());
  }

  /**
   * Toggles a single row from its checkbox without applying plain-click replacement.
   *
   * @param event - Click event.
   * @param row - Data row.
   */
  protected onRowCheckboxClick(event: MouseEvent, row: Record<string, unknown>): void {
    event.stopPropagation();
    this.selectionStore.toggleRow(row, this.sortPath());
  }

  /**
   * Applies Drive/OneDrive click semantics to a row body click.
   *
   * @param event - Mouse event.
   * @param rowIndex - Index within this table's `rows()`.
   */
  protected onRowClick(event: MouseEvent, rowIndex: number): void {
    if (!this.selectionEnabled()) {
      return;
    }
    const target: HTMLElement = event.target as HTMLElement;
    if (target.closest('button, mat-checkbox, .dg-checkbox-cell, .dg-fly-to-btn') !== null) {
      return;
    }
    this.selectionStore.applyClick(
      this.rows(),
      rowIndex,
      { ctrlKey: event.ctrlKey, metaKey: event.metaKey, shiftKey: event.shiftKey },
      this.sortPath()
    );
  }

  /**
   * Track function for row loops.
   *
   * @param index - Loop index fallback.
   * @param row - Data row.
   * @returns Stable identity.
   */
  protected trackRow(index: number, row: Record<string, unknown>): unknown {
    const id: unknown = row['assetId'];
    return id !== undefined ? id : index;
  }

  /**
   * Display-ready cell value.
   *
   * @param row - Data row.
   * @param field - Column field.
   * @returns Display value, or null.
   */
  protected cellValue(row: Record<string, unknown>, field: string): string | number | null {
    return getCellValue(row, field);
  }

  /**
   * Raw coded cell value.
   *
   * @param row - Data row.
   * @param field - Column field.
   * @returns Raw value.
   */
  protected cellRawValue(row: Record<string, unknown>, field: string): unknown {
    return getCellRawValue(row, field);
  }

  /**
   * Color for a numeric range chip.
   *
   * @param column - Column definition.
   * @param value - Raw cell value.
   * @returns Color, or null.
   */
  protected colorForValue(column: DataGridColumn, value: unknown): string | null {
    return getColorForValue(column, value);
  }

  /**
   * Discriminator chip color for a type name.
   *
   * @param typeName - Slug or display name.
   * @returns Color, or null.
   */
  protected discriminatorColor(typeName: string): string | null {
    return resolveDiscriminatorColor(typeName, this.discriminatorColors(), this.discriminatorLabels());
  }

  /**
   * Human-friendly discriminator label.
   *
   * @param slug - Raw type slug.
   * @returns Display name, or the slug.
   */
  protected displayName(slug: string): string {
    return this.discriminatorLabels()[slug] ?? slug;
  }

  /**
   * Requests the next page when the user scrolls near the end of loaded rows.
   *
   * @param index - First visible item index.
   */
  protected onScrollIndexChange(index: number): void {
    const sortPath: string = this.sortPath();
    const rowCount: number = this.rowCount();
    const totalCount: number = this.totalCount();
    const hasMore: boolean =
      sortPath === DATA_GRID_FLAT_SORT_PATH
        ? this.hasMore()
        : groupHasMorePages(sortPath, rowCount, totalCount, this.groupBucketStates());

    let viewportRows: number;
    if (sortPath === DATA_GRID_FLAT_SORT_PATH) {
      const viewport: CdkVirtualScrollViewport | undefined = this.viewport();
      viewportRows = Math.ceil((viewport?.getViewportSize() ?? 0) / this.rowHeight());
    } else {
      viewportRows = Math.ceil(this.leafViewportHeight(rowCount) / this.rowHeight());
    }

    if (
      !shouldLoadMoreOnScroll(
        hasMore,
        this.loading(),
        this.loadingMore(),
        index,
        viewportRows,
        rowCount,
        DATA_GRID_LOAD_MORE_BUFFER
      )
    ) {
      return;
    }
    this.loadMore.emit(resolveLoadMoreValue(sortPath, this.primaryGroupField()));
  }

  /** Remeasures the CDK viewport after layout or data changes. */
  private refreshViewport(): void {
    this.viewport()?.checkViewportSize();
  }

  /** Observes the table wrapper so a 0-height viewport is remeasured when layout settles. */
  private observeTableWrapperSize(): void {
    afterNextRender((): void => {
      const wrapper: HTMLElement | undefined = this.tableWrapper()?.nativeElement;
      if (wrapper === undefined) {
        return;
      }
      const resizeObserver: ResizeObserver = new ResizeObserver((): void => {
        this.refreshViewport();
      });
      resizeObserver.observe(wrapper);
      this.destroyRef.onDestroy((): void => resizeObserver.disconnect());
    });
  }
}
